/** @module csi/widget/_svg/Events */
define([
	"dojo/_base/declare",
	"dojo/_base/event",
	"dojo/_base/lang",
	"dojo/has",
	"dojo/touch",
	"dojo/on",
	"dijit/focus",
	"csi/dndHTML5",
	"../../../canvas/widgets/interfaces/model/enumerations/Keys",
	"csi/widget/_svg/MouseEvent",
	"csi/widget/_svg/util",
], function(declare, event, lang, has, touch, on, focus, dndHTML5, {Keys}, MouseEvent, util) {

	const isSafari = has("safari");

	// set up keys
	const {
		DOWN,
		ENTER,
		ESCAPE,
		UP,
		BACKSPACE,
		DELETE,
		KEY_A,
		KEY_C,
		KEY_G,
		KEY_L,
		KEY_O,
		KEY_P,
		KEY_R,
		KEY_S,
		KEY_T,
		KEY_V,
		KEY_X,
		KEY_Y,
		KEY_Z,
		SPACE_BAR,
	} = Keys

	const CSI_COPY_PASTE = "csi/csiEventCopyPaste";
	const GRAB = "grab";
	const GRABBING = "grabbing";
	const SELECT_MODE = "SELECT";

	/**
	 * Copy specified string to clipboard
	 * @param {string} txt
	 */
	function copyStringToClipboard(txt) {
		function handler(event) {
			if (event.clipboardData) {
				event.clipboardData.setData(CSI_COPY_PASTE, txt);
			}
			event.preventDefault();
			document.removeEventListener("copy", handler, true);
		}

		// Safari does not fire the handler if plain text is not selected
		if (isSafari) {
			$('input')[0].select();
		}

		document.addEventListener("copy", handler, true);
		document.execCommand("copy");
	}

	return declare(null, {
		// SvgCanvas: editor object
		editor: null,

		// Boolean: Used to indicate whether mouse is down or not, mainly used for to determine whether curve point is in drag mode or not
		// if it's in drag mode then do not allowing keyboard shortcuts for curve
		isMouseDown: false,

		// Boolean: indicates if mouse is over canvas editor
		_bOverEditor: false,

		// Boolean: indicates if mouse is over SVG node
		_bOverSvg: false,

		/** @property {Object[]} _handles - event handles */
		_handles: null,


		/**
		 * Set up handlers
		 * @param {Object} editor 
		 */
		constructor: function(editor) {
			// for MouseEvent.js to access editor
			this.editor = editor;

			// attach to events
			this._handles = [
				on(document, "keydown", lang.hitch(this, this.onKeyDown)),
				on(document, "keyup", lang.hitch(this, this.onKeyUp)),
				on(document, "mouseup", lang.hitch(this, this.onMouseUp)),

				// dojo/touch event
				on(document, touch.move, lang.hitch(this, this.onMouseMove)),
				on(document, touch.press, lang.hitch(this, this.onMouseDown))
			];

			// Resolve conflict svg vs image paste.
			// When user copy content of canvas to svg clipboard, we also put special string to system clipboard.
			// When user do Paste, we analyze content of system clipboard, and if it match this special string, we
			// activate svg paste, if not - we allow system Paste work (Paste Images)
			this._handles.push(on(document, "paste", lang.hitch(this, this.onPaste)));
		},

		/**
		 * Cleanup event handles
		 */
		cleanup: function() {
			this._handles.forEach(function(h) {
				h.remove();
			});
		},

		mouseEvent: function(/*Event*/e) {
			// summary: create mouse event with (x,y) coordinates constrained to canvas
			return new MouseEvent(e, this.editor, false);
		},

		/**
		 * When an attribute or image is dragged onto the canvas, remove all highlights
		 * @param {Event} e
		 */
		onDragEnd: function(/*Event*/e) {
			this.editor.removeHighlights();
		},

		/**
		 * On Space Bar up turn panning off if mouse is not down
		 * @param {MouseEvent} e 
		 */
		onKeyUp: function(e) {
			if (e.keyCode === SPACE_BAR) {
				const {editor} = this;
				if (!this.isMouseDown) {
					this.setPanningMode(false);
				}
				editor.insertNode.style.cursor = "";
			}
		},

		/**
		 * On Mouse move
		 * @param {MouseEvent} e 
		 */
		onMouseMove: function(e) {
			const me = this.mouseEvent(e);

			// track if over canvas only mouse move
			this._bOverEditor = me.overEditor;
			this._bOverSvg = me.overSvg;

			const {
				editor,
				mouseStart,
			} = this;

			// check if canvas is being dragged
			const isPanningCanvas = editor.canvasApp.pageHelper.getPanningMode();

			if (!this.isMouseDown && isPanningCanvas) {
				editor.insertNode.style.cursor = GRAB;
			} else if (isPanningCanvas && this.isMouseDown) {
				editor.insertNode.style.cursor = GRABBING;
				// limit pan to 60 fps
				requestAnimationFrame(() => {
					const {pageX, pageY} = e;
					const {parentElement} = editor.insertNode;

					const insertNodeBound = editor.insertNode.getBoundingClientRect();

					if (mouseStart) {
						const delta = {
							x: mouseStart.x - (pageX - insertNodeBound.left),
							y: mouseStart.y - (pageY - insertNodeBound.top),
						}

						parentElement.scrollBy(delta.x, delta.y);
					}
				});
			}
		},

		/**
		 * Mouse down event on document
		 * @param e {Event}
		 */
		onMouseDown: function(e) {
			this.isMouseDown = true;

			const {editor} = this;
			const isPanningCanvas = editor.canvasApp.pageHelper.getPanningMode();
			if (isPanningCanvas) {
				const {insertNode} = editor.view;
				const insertNodeBound = insertNode.getBoundingClientRect();;
				this.mouseStart = {
					x: e.pageX - insertNodeBound.left,
					y: e.pageY - insertNodeBound.top,
				};
			}
		},

		/**
		 * Mouse up event on canvas container
		 * @param e {Event}
		 */
		onMouseUp: function(e) {
			this.isMouseDown = false;
			const { editor } = this;
			if (!editor.canvasApp.pageHelper.getPanningMode()) {
				editor.insertNode.style.cursor = "";
			}
		},

		/**
		 * On key down handler
		 * @param {KeyboardEvent} e 
		 */
		onKeyDown: function(e) {
			const {editor} = this;

			// summary: handle various keyboard Keys, but only if mouse is over canvas and we do not have other focused elements
			// C8-19609 -> if escape is pressed and the mouse has just resized a text box, we do not want this function to return here
			// C8-20290 -> if escape is pressed and we are in draw mode but not edit text mode, set canvas to select mode
			if (editor.isDrawCanvasMode()) {
				if (e.keyCode == ESCAPE) {
					editor.canvasApp.canvasApi().setCanvasMode(SELECT_MODE);
				}
				return;
			} else if (e.keyCode != ESCAPE && !this._bOverEditor) {
				return;
			} else if (!editor.isEditTextCanvasMode() && e.keyCode === SPACE_BAR) {
				e.preventDefault();
			}

			var view = editor.view;
			var focusNode = focus.curNode;

			var page = view.getSelectedRefNode();
			if ((e.target != focusNode || e.target == view.focusNode) && e.target.contentEditable !== "true") {
				// handle keyboard event on SVG canvas
				if (this.onKey(this.mouseEvent(e))) {
					event.stop(e);
				}
			} else if (e.target == focusNode) {
				// if the target is on the currently focused element, then that means it cannot be a canvas event as those elements do not receive focus;
				// it could be the currently selected page though, so do check for that and handle as appropriate
				if ((page == focusNode) && this._onPageKey(this.mouseEvent(e))) {
					event.stop(e);
				}
			}
		},

		/**
		 * Handle keyboard events for canvas page
			* @param {MouseEvent} me
		 */
		onKey: function(me) {
			var editor = this.editor;
			var view = editor.view;
			// if alt key is pressed return false
			if (me.alt()) {
				return false;
			}
			// Delete curve point if DELETE or BACKSPACE key is pressed, and in an appropriate edit mode
			if (editor.isEditCurveCanvasMode() ||
				editor.isEditCustomCurve() ||
				editor.isEditPictureCanvasMode() ||
				editor.isEditPenMode()) {
				if (!me.ctrl() && !me.shift()) {
					switch (me.key()) {
						case BACKSPACE:
						case DELETE:
							// Don't allow deleting curve points if editing a picture unless it's cropped to a custom shape
							if (editor.isEditCustomCrop() || !editor.isEditPictureCanvasMode()) {
								// Delete selected curve point
								editor.canvasApp.pageApi().deleteCurvePoint();
							}
							return true;
						case ENTER:
						case ESCAPE:
							// Toggle Edit curve mode
							editor.canvasApp.canvasApi().editPageObject(false, true);
							return true;
					}
				}
				// CTRL + Z/Y for Redo/Undo
				if (me.ctrl() && !me.shift()) {
					switch (me.key()) {
						case KEY_Y:
							editor.redo();
							return true;
						case KEY_Z:
							editor.undo();
							return true;
					}
				}
				// CTRL + SHIFT + Z for Redo
				if (me.ctrl() && me.shift() && me.key() === KEY_Z) {
					editor.redo();
					return true;
				}
			} else if (editor.isSubgroupSelected()) {
				if (me.key() == ESCAPE) {
					editor.canvasApp.selectionApi().clearSubgroupSelection();
					return true;
				}
			}

			// return if EDIT_CURVE mode is on
			if (this.editor.isEditCurveCanvasMode()) {
				return false;
			}

			// handle keys that only work in edit mode
			if (me.ctrl() && me.shift()) {
				switch (me.key()) {
					case KEY_G:
						editor.ungroupSelection();
						return true;
					case KEY_Z:
						editor.redo();
						return true;
				}
			} else if (me.ctrl()) {
				switch (me.key()) {
					case KEY_A:
						if (editor.isDefaultCanvasMode()) {
							editor.canvasApp.selectionApi().selectObjectsByIndex();
							editor.focus();
						}
						return true;
					case KEY_C:
						copyStringToClipboard(CSI_COPY_PASTE);
						editor.canvasApp.clipboardApi().copy();
						return true;
					case KEY_G:
						editor.groupSelection();
						return true;
					case KEY_S:
						view.save();
						return true;
					case KEY_X:
						editor.cutSelection();
						return true;
					case KEY_Y:
						editor.redo();
						return true;
					case KEY_Z:
						editor.undo();
						return true;
					case UP:
						editor.canvasApp.pageApi().changePageObjectOrder(true);
						return true;
					case DOWN:
						editor.canvasApp.pageApi().changePageObjectOrder(false);
						return true;
				}
			} else if (util.isArrowKeyPressed(me.key())) {
				editor.canvasApp.pageApi().moveObjects(me.key(), me.shift());
				return true;
			} else if (!me.shift() && (editor.isDefaultCanvasMode() || me.key() === ESCAPE)) {
				const canvasApi = editor.canvasApp.canvasApi();
				switch (me.key()) {
					case BACKSPACE:
					case DELETE:
						// delete selected objects
						editor.deleteShapes();
						return true;
					case ENTER: {
						canvasApi.editPageObject();
						return true;
					}
					case ESCAPE: {
						const setToSelectMode = !this.isMouseDown || !editor.isEditCurveCanvasMode();
						if (editor.fillingAttributeShapeId) {
							editor.fillingAttributeShapeId = false;
							editor.updateToolbar();
						}
						else if (setToSelectMode) {
							canvasApi.setCanvasMode(SELECT_MODE);
						}
						return setToSelectMode;
					}
					case KEY_L: {
						canvasApi.setCanvasMode(util.canvasModes.DRAW);
						editor.setDrawMode("line");
						break;
					}
					case KEY_O: {
						canvasApi.setCanvasMode(util.canvasModes.DRAW);
						editor.setDrawMode("ellipse");
						break;
					}
					case KEY_P: {
						canvasApi.setCanvasMode(util.canvasModes.DRAW);
						editor.setDrawMode("pen");
						break;
					}
					case KEY_R: {
						canvasApi.setCanvasMode(util.canvasModes.DRAW);
						editor.setDrawMode("rect");
						break;
					}
					case KEY_T: {
						canvasApi.setCanvasMode(util.canvasModes.DRAW);
						editor.setDrawMode("rect", true, true);
						break;
					}
					case SPACE_BAR: {
						if (editor.isEditMode() && !editor.canvasApp.pageHelper.getPanningMode()) {
							this.setPanningMode(true);
						}
						break;
					}
					default:
						break;
				}
			}
			return false;
		},

		/**
		 * Handle keyboard events for pages
			* @param {MouseEvent} me
		 */
		_onPageKey: function(me) {
			if (me.alt()) {
				return false;
			}
			// return if EDIT_CURVE mode is on
			if (this.editor.isEditCurveCanvasMode()) {
				return false;
			}

			var editor = this.editor;
			var view = editor.view;

			switch (me.key()) {
				case UP:
					view.selectNextPage(true);
					return true;
				case DOWN:
					view.selectNextPage();
					return true;
			}

			if (editor.isEditMode()) {
				if (me.ctrl() && me.shift()) {
					switch (me.key()) {
						case KEY_Z:
							editor.redo();
							return true;
					}
				} else if (me.ctrl()) {
					switch (me.key()) {
						case KEY_A:
							if (editor.isDefaultCanvasMode()) {
								editor.canvasApp.selectionApi().selectObjectsByIndex();
								editor.focus();
							}
							return true;
						case KEY_C:
							editor.copy(true);
							return true;
						case KEY_S:
							view.save();
							return true;
						case KEY_X:
							editor.cut();
							return true;
						case KEY_V:
							editor.paste();
							return true;
						case KEY_Y:
							editor.redo();
							return true;
						case KEY_Z:
							editor.undo();
							return true;
					}
				} else if (!me.shift()) {
					// handle keys that work whether in edit mode or not
					switch (me.key()) {
						case BACKSPACE:
						case DELETE:
							view.deletePage();
							return true;
						case ENTER:
							view.addPage();
							return true;
					}
					return false;
				}
			}
		},

		/**
		 * Do paste if our data is in the clipboard.
		 * @param {Event} ev
		 */
		onPaste: function(ev) {
			if (ev.clipboardData && (ev.clipboardData.getData(CSI_COPY_PASTE) == CSI_COPY_PASTE)) {
				this.editor.canvasApp.pageApi().paste();
				dndHTML5.stopEvent(ev);
			}
		},

		/**
		 * Set panning mode
		 * @param {Boolean} isPan
		 */
		setPanningMode: function(isPan) {
			const { editor } = this;
			editor.canvasApp.canvasApi().setPanningMode(isPan);
			editor.view.canvasWidgetNode.splitNode.set("frozen", isPan);
			const bodyStyle = document.body.style;
			const { style } = editor.insertNode;
			if (isPan) {
				style.cursor = GRAB;
				bodyStyle.userSelect = "none";
			} else {
				style.cursor = bodyStyle.userSelect = "";
			}
		}

	});
});
